import { API_ENDPOINTS } from '@/config/api-endpoints';
import { ApiContextType, ApiResponse } from '@/contexts/ApiContext';
import {
  ActionName,
  AVAILABLE_ACTIONS,
  AVAILABLE_RESOURCES,
  BulkUpdateUserPermissionsRequest,
  CreateRoleRequest,
  Permission,
  PermissionStatistics,
  ResourceName,
  RevokeUserPermissionsRequest,
  Role,
  UpdateRoleRequest,
  UpdateUserPermissionsRequest,
} from '@/types/api-responses';

export class RbacService {
  constructor(private api: ApiContextType) {}

  // Role Management
  async getRoles(): Promise<ApiResponse<{ roles: Role[] }>> {
    return this.api.get<{ roles: Role[] }>(API_ENDPOINTS.RBAC.GET_ROLES);
  }

  async createRole(roleData: CreateRoleRequest): Promise<ApiResponse<Role>> {
    return this.api.post<Role>(API_ENDPOINTS.RBAC.CREATE_ROLE, roleData);
  }

  async updateRole(roleData: UpdateRoleRequest): Promise<ApiResponse<Role>> {
    return this.api.put<Role>(API_ENDPOINTS.RBAC.UPDATE_ROLE, roleData);
  }

  async deleteRole(roleName: string): Promise<ApiResponse> {
    return this.api.delete(API_ENDPOINTS.RBAC.DELETE_ROLE(roleName));
  }

  // User Permission Management
  async updateUserPermissions(
    data: UpdateUserPermissionsRequest
  ): Promise<ApiResponse> {
    return this.api.put(API_ENDPOINTS.RBAC.UPDATE_USER_PERMISSIONS, data);
  }

  async revokeUserPermissions(
    data: RevokeUserPermissionsRequest
  ): Promise<ApiResponse> {
    return this.api.put(API_ENDPOINTS.RBAC.REVOKE_USER_PERMISSIONS, data);
  }

  async bulkUpdateUserPermissions(
    data: BulkUpdateUserPermissionsRequest
  ): Promise<
    ApiResponse<{
      results: Array<{
        userId: string;
        success: boolean;
        error?: string;
      }>;
    }>
  > {
    return this.api.put<{
      results: Array<{
        userId: string;
        success: boolean;
        error?: string;
      }>;
    }>(API_ENDPOINTS.RBAC.BULK_UPDATE_USER_PERMISSIONS, data);
  }

  // Permission Queries
  async getUserPermissions(
    userId: string
  ): Promise<ApiResponse<{ permissions: Permission[] }>> {
    return this.api.get<{ permissions: Permission[] }>(
      API_ENDPOINTS.RBAC.GET_USER_PERMISSIONS(userId)
    );
  }

  async getUsersWithPermission(params: {
    resource: ResourceName;
    action: ActionName;
  }): Promise<
    ApiResponse<{
      users: Array<{
        _id: string;
        firstName?: string;
        lastName?: string;
        email: string;
        roleName: string;
        role: string;
      }>;
    }>
  > {
    return this.api.get<{
      users: Array<{
        _id: string;
        firstName?: string;
        lastName?: string;
        email: string;
        roleName: string;
        role: string;
      }>;
    }>(
      `${API_ENDPOINTS.RBAC.GET_USERS_WITH_PERMISSION}?resource=${params.resource}&action=${params.action}`
    );
  }

  async getRoleHierarchy(): Promise<ApiResponse<{ hierarchy: Role[] }>> {
    return this.api.get<{ hierarchy: Role[] }>(
      API_ENDPOINTS.RBAC.GET_ROLE_HIERARCHY
    );
  }

  async getPermissionStatistics(): Promise<
    ApiResponse<{ stats: PermissionStatistics }>
  > {
    return this.api.get<{ stats: PermissionStatistics }>(
      API_ENDPOINTS.RBAC.GET_PERMISSION_STATISTICS
    );
  }

  // Utility methods with improved type safety
  formatPermissionKey(resource: ResourceName, action: ActionName): string {
    return `${resource}:${action}`;
  }

  hasPermission(
    userPermissions: Permission[],
    resource: ResourceName,
    action: ActionName
  ): boolean {
    // Check if user is SuperAdmin (should have 'all' resource with all actions)
    const allResourcePermission = userPermissions.find(
      perm => perm.resource === 'all'
    );
    if (allResourcePermission) {
      return true; // SuperAdmin has all permissions
    }

    // Check specific permission
    return userPermissions.some(
      permission =>
        permission.resource === resource && permission.actions.includes(action)
    );
  }

  mergePermissions(...permissionArrays: Permission[][]): Permission[] {
    const merged = new Map<string, Set<string>>();

    permissionArrays.forEach(permissions => {
      permissions.forEach(permission => {
        if (!merged.has(permission.resource)) {
          merged.set(permission.resource, new Set());
        }
        permission.actions.forEach(action => {
          merged.get(permission.resource)!.add(action);
        });
      });
    });

    return Array.from(merged.entries()).map(([resource, actions]) => ({
      _id: `${resource}_merged`,
      resource,
      actions: Array.from(actions),
    }));
  }

  validateRoleData(roleData: CreateRoleRequest | UpdateRoleRequest): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Validate role name (required for create, optional for update)
    if ('name' in roleData && roleData.name !== undefined) {
      if (!roleData.name || roleData.name.trim().length < 2) {
        errors.push('Role name must be at least 2 characters long');
      }

      if (!/^[a-zA-Z0-9_-]+$/.test(roleData.name)) {
        errors.push(
          'Role name can only contain letters, numbers, underscores, and hyphens'
        );
      }
    }

    // Validate permissions
    if (roleData.permissions) {
      if (roleData.permissions.length === 0) {
        errors.push('Role must have at least one permission');
      } else {
        roleData.permissions.forEach((permission, index) => {
          if (!permission.resource) {
            errors.push(`Permission ${index + 1}: Resource is required`);
          }

          // Validate resource is in allowed list
          if (
            permission.resource &&
            !AVAILABLE_RESOURCES.includes(permission.resource as ResourceName)
          ) {
            errors.push(
              `Permission ${index + 1}: Invalid resource '${permission.resource}'`
            );
          }

          if (!permission.actions || permission.actions.length === 0) {
            errors.push(
              `Permission ${index + 1}: At least one action is required`
            );
          }

          // Validate actions are in allowed list
          if (permission.actions) {
            const invalidActions = permission.actions.filter(
              action => !AVAILABLE_ACTIONS.includes(action as ActionName)
            );
            if (invalidActions.length > 0) {
              errors.push(
                `Permission ${index + 1}: Invalid actions: ${invalidActions.join(', ')}`
              );
            }
          }
        });
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Additional utility methods
  getAvailableResources(): ResourceName[] {
    return [...AVAILABLE_RESOURCES];
  }

  getAvailableActions(): ActionName[] {
    return [...AVAILABLE_ACTIONS];
  }

  getPermissionDisplayName(resource: ResourceName): string {
    return resource.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  getActionDisplayName(action: ActionName): string {
    return action.charAt(0).toUpperCase() + action.slice(1);
  }
}

// Export a factory function to create the service with API instance
export const createRbacService = (api: ApiContextType) => new RbacService(api);

// Export default service instance (will be initialized with API context)
export default RbacService;
